import { lazy, Suspense } from 'react'
import { createBrowserRouter, isRouteErrorResponse, Link, Navigate, useRouteError } from 'react-router'
import { authenticatedHomePath } from '@/config/env'
import { homePathFor, RequireAnyPermission, RequireAuth, RequirePermission, useAuth } from '@/features/auth'
import { RequireModule } from '@/shared/presentation/RequireModule'
import { RequireHomeModule } from '@/shared/presentation/RequireHomeModule'

const SmsPage = lazy(async () =>
  import('@/features/sms/presentation/SmsPage').then((module) => ({ default: module.SmsPage })),
)
const NotificationsPage = lazy(async () =>
  import('@/features/notifications/presentation/NotificationsPage').then((module) => ({
    default: module.NotificationsPage,
  })),
)
const NotificationPreferencesPage = lazy(async () =>
  import('@/features/notifications/presentation/NotificationPreferencesPage').then((module) => ({
    default: module.NotificationPreferencesPage,
  })),
)
const OperationsCenterPage = lazy(async () =>
  import('@/features/dashboard').then((module) => ({ default: module.OperationsCenterPage })),
)

const OperationsLivePage = lazy(async () =>
  import('@/features/operationsCenter').then((module) => ({ default: module.OperationsLivePage })),
)
const OperationsListPage = lazy(async () =>
  import('@/features/operationsCenter').then((module) => ({ default: module.OperationsListPage })),
)
const OperationDetailPage = lazy(async () =>
  import('@/features/operationsCenter').then((module) => ({ default: module.OperationDetailPage })),
)

const DesignSystemLabPage = lazy(async () =>
  import('@/design-system/lab').then((module) => ({ default: module.DesignSystemLabPage })),
)
const DesignSystemReferencePage = lazy(async () =>
  import('@/design-system/lab').then((module) => ({ default: module.DesignSystemReferencePage })),
)

const ClientsListPage = lazy(async () =>
  import('@/features/clients').then((module) => ({ default: module.ClientsListPage })),
)
const ClientDetailPage = lazy(async () =>
  import('@/features/clients').then((module) => ({ default: module.ClientDetailPage })),
)
const NewClientPage = lazy(async () =>
  import('@/features/clients').then((module) => ({ default: module.NewClientPage })),
)
const LeadDetailPage = lazy(async () =>
  import('@/features/leads').then((module) => ({ default: module.LeadDetailPage })),
)
const NewLeadPage = lazy(async () =>
  import('@/features/leads').then((module) => ({ default: module.NewLeadPage })),
)
const QuotesListPage = lazy(async () =>
  import('@/features/quotes').then((module) => ({ default: module.QuotesListPage })),
)
const SubmissionsPage = lazy(async () =>
  import('@/features/submissions').then((module) => ({ default: module.SubmissionsPage })),
)
const SubmissionsSettingsPage = lazy(async () =>
  import('@/features/submissions').then((module) => ({ default: module.SubmissionsSettingsPage })),
)
const DemandesV3Page = lazy(async () =>
  import('@/features/demandes-v3').then((module) => ({ default: module.DemandesV3Page })),
)
const DemandesV3DetailPage = lazy(async () =>
  import('@/features/demandes-v3').then((module) => ({ default: module.DemandesV3DetailPage })),
)
const DemandesV3SettingsPage = lazy(async () =>
  import('@/features/demandes-v3').then((module) => ({ default: module.DemandesV3SettingsPage })),
)
const QuoteDetailPage = lazy(async () =>
  import('@/features/quotes').then((module) => ({ default: module.QuoteDetailPage })),
)
const NewQuotePage = lazy(async () =>
  import('@/features/quotes').then((module) => ({ default: module.NewQuotePage })),
)
const ContractsListPage = lazy(async () =>
  import('@/features/contracts').then((module) => ({ default: module.ContractsListPage })),
)
const MarkersFieldPage = lazy(async () =>
  import('@/features/markers').then((module) => ({ default: module.MarkersFieldPage })),
)
const MarkersPage = lazy(async () =>
  import('@/features/markers').then((module) => ({ default: module.MarkersPage })),
)
const VnextContractsListPage = lazy(async () =>
  import('@/features/contracts-vnext').then((module) => ({ default: module.VnextContractsListPage })),
)
const ContractTypeChoicePage = lazy(async () =>
  import('@/features/contracts-vnext').then((module) => ({ default: module.ContractTypeChoicePage })),
)
const VnextContractWizardPage = lazy(async () =>
  import('@/features/contracts-vnext').then((module) => ({ default: module.VnextContractWizardPage })),
)
const ContractFlowSwitch = lazy(async () =>
  import('@/features/contracts-vnext').then((module) => ({ default: module.ContractFlowSwitch })),
)
const ServiceLocationPage = lazy(async () =>
  import('@/features/contracts-vnext').then((module) => ({ default: module.ServiceLocationPage })),
)
const NewContractPage = lazy(async () =>
  import('@/features/contracts').then((module) => ({ default: module.NewContractPage })),
)
const ContractDetailPage = lazy(async () =>
  import('@/features/contracts').then((module) => ({ default: module.ContractDetailPage })),
)
const ContractRedirectPage = lazy(async () =>
  import('@/features/contracts').then((module) => ({ default: module.ContractRedirectPage })),
)
const ContractSettingsPage = lazy(async () =>
  import('@/features/contracts').then((module) => ({ default: module.ContractSettingsPage })),
)
const MissionsPage = lazy(async () =>
  import('@/features/missions').then((module) => ({ default: module.MissionsPage })),
)
const MissionDetailPage = lazy(async () =>
  import('@/features/missions').then((module) => ({ default: module.MissionDetailPage })),
)
const DetectionProfilePage = lazy(async () =>
  import('@/features/missions').then((module) => ({ default: module.DetectionProfilePage })),
)
const RoutesPlanningPage = lazy(async () =>
  import('@/features/routes').then((module) => ({ default: module.RoutesPlanningPage })),
)
const FlagWalkPage = lazy(async () =>
  import('@/features/routes').then((module) => ({ default: module.FlagWalkPage })),
)
const RoutesModulePage = lazy(async () =>
  import('@/features/routes/module').then((module) => ({ default: module.RoutesModulePage })),
)
const DispatchPage = lazy(async () =>
  import('@/features/dispatch').then((module) => ({ default: module.DispatchPage })),
)
const InvoiceDetailPage = lazy(async () =>
  import('@/features/finance').then((module) => ({ default: module.InvoiceDetailPage })),
)
// Sprint H, section B : `InvoicesListPage`/`PaymentsListPage` restent
// exportés par `@/features/finance` (section 33 : jamais de suppression de
// composant) mais ne sont plus routés directement — `/invoices` et
// `/payments` redirigent vers `/facturation`, même patron que
// `ContractsListPage` en Sprint G.
const FacturationPage = lazy(async () =>
  import('@/features/finance').then((module) => ({ default: module.FacturationPage })),
)
const SettingsPage = lazy(async () =>
  import('@/features/settings').then((module) => ({ default: module.SettingsPage })),
)
const LoginPage = lazy(async () =>
  import('@/features/auth').then((module) => ({ default: module.LoginPage })),
)
const ForgotPasswordPage = lazy(async () =>
  import('@/features/auth').then((module) => ({ default: module.ForgotPasswordPage })),
)
const ResetPasswordPage = lazy(async () =>
  import('@/features/auth').then((module) => ({ default: module.ResetPasswordPage })),
)
const ForcePasswordChangePage = lazy(async () =>
  import('@/features/auth').then((module) => ({ default: module.ForcePasswordChangePage })),
)
const EmployeesPage = lazy(async () =>
  import('@/features/employees').then((module) => ({ default: module.EmployeesPage })),
)
const ReportsPage = lazy(async () =>
  import('@/features/reports').then((module) => ({ default: module.ReportsPage })),
)
const ForbiddenPage = lazy(async () =>
  import('@/features/auth').then((module) => ({ default: module.ForbiddenPage })),
)
const PublicPaymentPage = lazy(async () =>
  import('@/features/payment-portal').then((module) => ({ default: module.PublicPaymentPage })),
)
// Page publique unique de proposition (courriel `/contrat?t=…`, SMS `/p/:code`).
const PublicProposalPage = lazy(async () =>
  import('@/features/contract-portal').then((module) => ({ default: module.PublicProposalPage })),
)
const ShortLinkPage = lazy(async () =>
  import('@/features/contract-portal').then((module) => ({ default: module.ShortLinkPage })),
)
// Page personnelle des alertes SMS (invitation par courriel).
const SmsConsentPage = lazy(async () =>
  import('@/features/contract-portal').then((module) => ({ default: module.SmsConsentPage })),
)
const RenewalsPage = lazy(async () =>
  import('@/features/renewals').then((module) => ({ default: module.RenewalsPage })),
)
const SeasonEntryQualityPage = lazy(async () =>
  import('@/features/season-entry').then((module) => ({ default: module.SeasonEntryQualityPage })),
)
const FeedbackInboxPage = lazy(async () =>
  import('@/features/feedback').then((module) => ({ default: module.FeedbackInboxPage })),
)
const EmailInboxPage = lazy(async () =>
  import('@/features/email').then((module) => ({ default: module.EmailInboxPage })),
)
const SectorsPage = lazy(async () =>
  import('@/features/sectors').then((module) => ({ default: module.SectorsPage })),
)
const PublicationReviewPage = lazy(async () =>
  import('@/features/sectors').then((module) => ({ default: module.PublicationReviewPage })),
)
const SectorEditorPage = lazy(async () =>
  import('@/features/sectors').then((module) => ({ default: module.SectorEditorPage })),
)
const GooglePage = lazy(async () =>
  import('@/features/google').then((module) => ({ default: module.GooglePage })),
)
const OverviewMapPage = lazy(async () =>
  import('@/features/overview').then((module) => ({ default: module.OverviewMapPage })),
)
const OverviewV3Page = lazy(async () =>
  import('@/features/overview').then((module) => ({ default: module.OverviewV3Page })),
)
const OverviewMapV1Page = lazy(async () =>
  import('@/features/overview').then((module) => ({ default: module.OverviewMapV1Page })),
)

function lazyPage(element: React.ReactNode, label?: string) {
  return (
    <Suspense fallback={<RouteLoadingFallback {...(label === undefined ? {} : { label })} />}>
      {element}
    </Suspense>
  )
}

function RouteLoadingFallback({ label }: { label?: string }) {
  return (
    <main className="route-state" aria-busy="true" aria-live="polite">
      {label ? `Chargement — ${label}` : 'Chargement…'}
    </main>
  )
}

function RouteErrorFallback() {
  const error = useRouteError()
  const title = isRouteErrorResponse(error) && error.status === 404 ? 'Page introuvable' : 'Page indisponible'

  return (
    <main className="route-state" aria-labelledby="route-error-title">
      <div>
        <h1 id="route-error-title">{title}</h1>
        <Link to="/vue-ensemble">Retourner à l’accueil</Link>
      </div>
    </main>
  )
}

function HomeRedirect() {
  const { session, loading } = useAuth()
  if (loading) return null
  return <Navigate to={homePathFor(session, authenticatedHomePath)} replace />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <HomeRedirect />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/paiement',
    element: lazyPage(<PublicPaymentPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/contrat',
    element: lazyPage(<PublicProposalPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/p/:code',
    element: lazyPage(<ShortLinkPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/alertes',
    element: lazyPage(<SmsConsentPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/login',
    element: lazyPage(<LoginPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/forgot-password',
    element: lazyPage(<ForgotPasswordPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: '/auth/reset-password',
    element: lazyPage(<ResetPasswordPage />),
    errorElement: <RouteErrorFallback />,
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: '/compte/mot-de-passe',
        element: lazyPage(<ForcePasswordChangePage />, 'Mot de passe'),
      },
      {
        // Ancien tableau de bord, renommé « Centre des opérations 2 » et désactivable (ADR-019 §9).
        path: '/operations',
        element: lazyPage(
          <RequirePermission permission="dashboard.read">
            <RequireModule moduleKey="centre_operations_2" label="Centre des opérations 2">
              <OperationsCenterPage />
            </RequireModule>
          </RequirePermission>,
          'Centre des opérations 2',
        ),
      },
      {
        // Nouveau Centre des opérations, plein écran (ADR-019).
        path: '/centre-operations',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <RequireModule moduleKey="centre_operations" label="Centre des opérations">
              <OperationsLivePage />
            </RequireModule>
          </RequirePermission>,
          'Centre des opérations',
        ),
      },
      {
        // Module Opérations (mandat du 17 oct. 2026) : gestion administrative, mêmes données que le Centre.
        // `/operations` reste l'ancien « Centre des opérations 2 ».
        path: '/operations-deneigement',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <RequireModule moduleKey="centre_operations" label="Opérations">
              <OperationsListPage />
            </RequireModule>
          </RequirePermission>,
          'Opérations',
        ),
      },
      {
        path: '/operations-deneigement/:operationId',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <RequireModule moduleKey="centre_operations" label="Opérations">
              <OperationDetailPage />
            </RequireModule>
          </RequirePermission>,
          'Opération',
        ),
      },
      {
        // Vue d'ensemble : vue du 12 oct. 2026, accueil par défaut (noms inversés avec -2 le 31 oct. 2026).
        // Désactivée, l'adresse mène au premier accueil actif et autorisé.
        path: '/vue-ensemble',
        element: lazyPage(
          <RequireHomeModule moduleKey="vue_ensemble">
            <RequirePermission permission="dashboard.read">
              <OverviewMapPage />
            </RequirePermission>
          </RequireHomeModule>,
          'Vue d’ensemble',
        ),
      },
      {
        // Vue d'ensemble-2 : vue V3 (Contrats et demandes, Finances, Opérations), noms inversés le 31 oct. 2026.
        path: '/vue-ensemble-2',
        element: lazyPage(
          <RequirePermission permission="dashboard.read">
            <RequireModule moduleKey="vue_ensemble_2" label="Vue d’ensemble-2">
              <OverviewV3Page />
            </RequireModule>
          </RequirePermission>,
          'Vue d’ensemble-2',
        ),
      },
      {
        // Vue d'ensemble-1 : carte territoriale seule d'avant le 12 oct. 2026.
        path: '/vue-ensemble-1',
        element: lazyPage(
          <RequirePermission permission="dashboard.read">
            <RequireModule moduleKey="vue_ensemble_1" label="Vue d’ensemble-1">
              <OverviewMapV1Page />
            </RequireModule>
          </RequirePermission>,
          'Vue d’ensemble-1',
        ),
      },
      {
        // Sprint E, section 6 : « Leads » et « Soumissions » (Sprint C) sont
        // unifiés dans un seul module `/soumissions`. Redirection de
        // compatibilité, comme le fait déjà `/quotes` ci-dessous — pas deux
        // interfaces concurrentes.
        path: '/leads',
        element: <Navigate to="/soumissions?vue=a-faire" replace />,
      },
      {
        path: '/soumissions',
        element: lazyPage(
          <RequirePermission permission="lead.read">
            <SubmissionsPage />
          </RequirePermission>,
          'Demandes-V3',
        ),
      },
      {
        path: '/soumissions/parametres',
        element: lazyPage(
          <RequirePermission permission="settings.read">
            <SubmissionsSettingsPage />
          </RequirePermission>,
          'Réglages des demandes',
        ),
      },
      // Liste V3 (mandat du 30 oct. 2026), nommée « Demandes » depuis le 31 oct. 2026 (noms inversés avec /soumissions).
      // Module distinct : `/soumissions` et `/leads/:leadId` restent inchangés.
      {
        path: '/demandes-v3',
        element: lazyPage(
          <RequirePermission permission="lead.read">
            <DemandesV3Page />
          </RequirePermission>,
          'Demandes',
        ),
      },
      {
        path: '/demandes-v3/parametres',
        element: lazyPage(
          <RequirePermission permission="settings.read">
            <DemandesV3SettingsPage />
          </RequirePermission>,
          'Réglages des demandes',
        ),
      },
      {
        path: '/demandes-v3/:leadId',
        element: lazyPage(
          <RequirePermission permission="lead.read">
            <DemandesV3DetailPage />
          </RequirePermission>,
          'Fiche demande',
        ),
      },
      {
        path: '/leads/new',
        element: lazyPage(
          <RequirePermission permission="lead.create">
            <NewLeadPage />
          </RequirePermission>,
          'Nouvelle demande',
        ),
      },
      {
        path: '/leads/:leadId',
        element: lazyPage(
          <RequirePermission permission="lead.read">
            <LeadDetailPage />
          </RequirePermission>,
          'Fiche demande',
        ),
      },
      {
        // Sprint E, section 6 : `/quotes` (Sprint C, `PendingContractsListPage`)
        // est superseded par le module unifié `/soumissions`. Redirection de
        // compatibilité — `/quotes/legacy` (table `quotes` historique) reste
        // intacte, ce n'est pas la même destination.
        path: '/quotes',
        element: <Navigate to="/soumissions" replace />,
      },
      {
        path: '/quotes/legacy',
        element: lazyPage(
          <RequirePermission permission="quote.read">
            <QuotesListPage />
          </RequirePermission>,
          'Soumissions',
        ),
      },
      {
        path: '/quotes/new',
        element: lazyPage(
          <RequirePermission permission="quote.create">
            <NewQuotePage />
          </RequirePermission>,
          'Nouvelle soumission',
        ),
      },
      {
        path: '/quotes/:quoteId',
        element: lazyPage(
          <RequirePermission permission="quote.read">
            <QuoteDetailPage />
          </RequirePermission>,
          'Fiche soumission',
        ),
      },
      {
        path: '/clients',
        element: lazyPage(
          <RequirePermission permission="client.read">
            <ClientsListPage />
          </RequirePermission>,
          'Clients',
        ),
      },
      {
        path: '/clients/new',
        element: lazyPage(
          <RequirePermission permission="client.create">
            <NewClientPage />
          </RequirePermission>,
          'Nouveau client',
        ),
      },
      {
        path: '/clients/:clientId',
        element: lazyPage(
          <RequirePermission permission="client.read">
            <ClientDetailPage />
          </RequirePermission>,
          'Fiche client',
        ),
      },
      {
        // Contrats vNext : fiche et configuration d'une adresse de service.
        path: '/clients/:clientId/contracts/:contractId/adresses/:locationId',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <ServiceLocationPage />
          </RequirePermission>,
          'Adresse de service',
        ),
      },
      {
        // Sprint G, section A : la fiche Contrat vit désormais sous la fiche
        // Client — même composant `ContractDetailPage` que l'ancienne route
        // `/contracts/:contractId`, avec le `clientId` de l'URL en plus pour
        // le fil d'Ariane/lien retour ET une vérification défensive que le
        // Contrat appartient bien à ce Client (section 34-35 de la tâche).
        path: '/clients/:clientId/contracts/:contractId',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            {/* Contrats vNext : un Contrat Commercial / Multilogements ouvre sa propre fiche. */}
            <ContractFlowSwitch legacy={<ContractDetailPage />} />
          </RequirePermission>,
          'Fiche contrat',
        ),
      },
      {
        path: '/renewals',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <RenewalsPage />
          </RequirePermission>,
          'Renouvellements',
        ),
      },
      {
        path: '/season-entry',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <SeasonEntryQualityPage />
          </RequirePermission>,
          'Qualité saison',
        ),
      },
      {
        path: '/feedback',
        element: lazyPage(
          <RequirePermission permission="feedback.manage">
            <FeedbackInboxPage />
          </RequirePermission>,
          'Commentaires',
        ),
      },
      {
        path: '/emails',
        element: lazyPage(
          <RequireAnyPermission permissions={['email.read', 'email.manage']}>
            <EmailInboxPage />
          </RequireAnyPermission>,
          'Courriels',
        ),
      },
      {
        // Module Google : réservé à `settings.manage` jusqu'aux permissions `google.*` (plan, tâche 3).
        path: '/google',
        element: lazyPage(
          <RequirePermission permission="settings.manage">
            <GooglePage />
          </RequirePermission>,
          'Google',
        ),
      },
      {
        path: '/sms',
        element: lazyPage(
          <RequireModule moduleKey="alertes_sms" label="Alertes SMS">
            <RequireAnyPermission permissions={['sms.read', 'sms.settings']}>
              <SmsPage />
            </RequireAnyPermission>
          </RequireModule>,
          'Alertes SMS',
        ),
      },
      {
        path: '/contracts',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <ContractsListPage />
          </RequirePermission>,
          'Contrats',
        ),
      },
      {
        path: '/balises/terrain',
        element: lazyPage(
          <RequirePermission permission="marker.read">
            <MarkersFieldPage />
          </RequirePermission>,
          'Balises — terrain',
        ),
      },
      {
        path: '/balises',
        element: lazyPage(
          <RequirePermission permission="marker.read">
            <MarkersPage />
          </RequirePermission>,
          'Balises',
        ),
      },
      {
        path: '/contracts/new',
        element: lazyPage(
          <RequirePermission permission="contract.create">
            <NewContractPage />
          </RequirePermission>,
          'Nouveau contrat',
        ),
      },
      {
        // Contrats vNext (docs/commercial/contrats-vnext.md). Déclarée avant
        // `/contracts/:contractId` : « commercial » et « multilogements » ne sont pas des numéros.
        path: '/contracts/nouveau',
        element: lazyPage(
          <RequirePermission permission="contract.create">
            <ContractTypeChoicePage />
          </RequirePermission>,
          'Nouveau contrat',
        ),
      },
      {
        path: '/contracts/new/commercial',
        element: lazyPage(
          <RequirePermission permission="contract.create">
            <VnextContractWizardPage kind="COMMERCIAL" />
          </RequirePermission>,
          'Nouveau contrat commercial',
        ),
      },
      {
        path: '/contracts/new/multilogements',
        element: lazyPage(
          <RequirePermission permission="contract.create">
            <VnextContractWizardPage kind="MULTIFAMILY" />
          </RequirePermission>,
          'Nouveau contrat multilogements',
        ),
      },
      {
        path: '/contracts/commercial',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <VnextContractsListPage kind="COMMERCIAL" />
          </RequirePermission>,
          'Contrats commerciaux',
        ),
      },
      {
        path: '/contracts/multilogements',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <VnextContractsListPage kind="MULTIFAMILY" />
          </RequirePermission>,
          'Contrats multilogements',
        ),
      },
      {
        // Déclarée avant `/contracts/:contractId` : « settings » n'est pas un
        // numéro de Contrat, et la route paramétrée l'avalerait.
        path: '/contracts/settings',
        element: lazyPage(
          <RequirePermission permission="contract.settings_manage">
            <ContractSettingsPage />
          </RequirePermission>,
          'Réglages des contrats',
        ),
      },
      {
        // Sprint G, section B : lien profond de compatibilité — résout le
        // `client_id` du Contrat puis redirige vers
        // `/clients/:clientId/contracts/:contractId`. Ne remplace pas la
        // vérification RLS/composant de cette dernière, juste un aiguillage.
        path: '/contracts/:contractId',
        element: lazyPage(
          <RequirePermission permission="contract.read">
            <ContractRedirectPage />
          </RequirePermission>,
          'Contrat',
        ),
      },
      {
        // Missions démarrées sur la tablette (bureau). L'opérateur exécute les siennes dans reca-operator-v3.
        path: '/missions',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <MissionsPage />
          </RequirePermission>,
          'Missions',
        ),
      },
      {
        // Profil de détection (ADR-018) : lecture `mission.read`, publication `mission.supervise` dans la page.
        path: '/missions/detection',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <DetectionProfilePage />
          </RequirePermission>,
          'Détection automatique',
        ),
      },
      {
        path: '/missions/:missionId',
        element: lazyPage(
          <RequirePermission permission="mission.read">
            <MissionDetailPage />
          </RequirePermission>,
          'Fiche mission',
        ),
      },
      // Module de gestion (liste, outils, historique) et éditeur cartographique plein écran
      // (docs/routes/README.md §8). L'éditeur, jusqu'au 11 oct. 2026 à `/routes`, est à `/routes/editor`.
      {
        path: '/routes',
        element: lazyPage(
          <RequirePermission permission="route.read">
            <RoutesModulePage />
          </RequirePermission>,
          'Routes',
        ),
      },
      {
        path: '/routes/editor',
        element: lazyPage(
          <RequirePermission permission="route.read">
            <RoutesPlanningPage />
          </RequirePermission>,
          'Éditeur de routes',
        ),
      },
      // Routes › Répartition (docs/routes/repartition/) : plan multi-tracteurs plein écran. Lecture `dispatch.read`;
      // brouillon et publication contrôlés aussi par le serveur (`dispatch.edit`, `dispatch.publish`).
      {
        path: '/routes/repartition',
        element: lazyPage(
          <RequirePermission permission="dispatch.read">
            <DispatchPage />
          </RequirePermission>,
          'Répartition des routes',
        ),
      },
      // Module Secteurs (docs/secteurs/README.md) : la visibilité du module masque la navigation, jamais les données
      // publiées ni Routes, Missions et Opérations. Écritures du brouillon contrôlées par le serveur (`sector.edit`).
      {
        path: '/secteurs',
        element: lazyPage(
          <RequireModule moduleKey="secteurs" label="Secteurs">
            <RequirePermission permission="sector.read">
              <SectorsPage />
            </RequirePermission>
          </RequireModule>,
          'Secteurs',
        ),
      },
      {
        // Relecture accessible en lecture; publier exige `sector.publish`, contrôlé aussi par le serveur.
        path: '/secteurs/publication',
        element: lazyPage(
          <RequireModule moduleKey="secteurs" label="Secteurs">
            <RequirePermission permission="sector.read">
              <PublicationReviewPage />
            </RequirePermission>
          </RequireModule>,
          'Relire et publier',
        ),
      },
      {
        path: '/secteurs/contour',
        element: lazyPage(
          <RequireModule moduleKey="secteurs" label="Secteurs">
            <RequirePermission permission="sector.read">
              <SectorEditorPage />
            </RequirePermission>
          </RequireModule>,
          'Contour du secteur',
        ),
      },
      // Ajustement maison par maison (docs/routes/README.md §9) : plein écran, écritures réservées à route.update.
      {
        path: '/routes/maison-par-maison',
        element: lazyPage(
          <RequirePermission permission="route.read">
            <FlagWalkPage />
          </RequirePermission>,
          'Ajustement maison par maison',
        ),
      },
      {
        path: '/employes',
        element: lazyPage(
          <RequirePermission permission="employee.read">
            <EmployeesPage />
          </RequirePermission>,
          'Employés',
        ),
      },
      // Module Rapports (docs/rapports/README.md) : chaque onglet vérifie sa propre permission.
      {
        path: '/rapports',
        element: lazyPage(
          <RequirePermission permission="report.read">
            <ReportsPage />
          </RequirePermission>,
          'Mes Couilles',
        ),
      },
      {
        path: '/settings',
        element: lazyPage(
          <RequirePermission permission="settings.read">
            <SettingsPage />
          </RequirePermission>,
          'Paramètres',
        ),
      },
      {
        // Consolidé dans `/settings` (onglet Taxes) — même patron que
        // `/invoices`→`/facturation` (Sprint H). Route conservée par
        // compatibilité de lien.
        path: '/settings/quotes/taxes',
        element: <Navigate to="/settings?tab=taxes" replace />,
      },
      {
        // Sprint H, section A/B/I : « Factures » et « Paiements » fusionnent
        // en un seul module `/facturation` à onglets. Redirection de
        // compatibilité — même patron que `/quotes`→`/soumissions` (Sprint E)
        // et `/contracts`→`/clients` (Sprint G).
        path: '/invoices',
        element: <Navigate to="/facturation?view=factures" replace />,
      },
      {
        path: '/invoices/:invoiceId',
        element: lazyPage(
          <RequirePermission permission="invoice.read">
            <InvoiceDetailPage />
          </RequirePermission>,
          'Fiche facture',
        ),
      },
      {
        path: '/payments',
        element: <Navigate to="/facturation?view=paiements" replace />,
      },
      {
        path: '/facturation',
        element: lazyPage(
          <RequireAnyPermission permissions={['invoice.read', 'payment.read']}>
            <FacturationPage />
          </RequireAnyPermission>,
          'Facturation',
        ),
      },
      // Notifications personnelles : aucune permission propre, chaque ligne suit la permission de son module.
      { path: '/notifications', element: lazyPage(<NotificationsPage />, 'Notifications') },
      {
        path: '/notifications/preferences',
        element: lazyPage(<NotificationPreferencesPage />, 'Préférences de notification'),
      },
      { path: '/forbidden', element: lazyPage(<ForbiddenPage />) },
    ],
  },
  {
    path: '/design-system',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <DesignSystemLabPage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
    handle: {
      title: 'Laboratoire du Design System',
      module: 'design-system',
    },
  },
  {
    // Référence visuelle commune des modules récents (11 oct. 2026) : catalogue des vrais composants.
    path: '/design-system/reference',
    element: (
      <Suspense fallback={<RouteLoadingFallback />}>
        <DesignSystemReferencePage />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
    handle: {
      title: 'Référence visuelle RECA',
      module: 'design-system',
    },
  },
])
